<template>
  <div class="ProductModal" :class="{ 'ProductModal--Active': background }">
    <div
      class="ProductModal__Wrapper"
      :class="[modalClasses, { 'ProductModal__Wrapper--Active': modal }]"
    >
      <button role="button" @click="handleClose" class="ProductModal__Close">
        <Icon :icon="'close'" />
      </button>
      <div class="Product page-width">
        <Gallery
          v-if="modal && data"
          :url="url"
          :location="location"
          :isModal="modal"
          :disable-zoom="true"
          :gallery="data.gallery"
          :title="data.item.title"
          :shortDescription="shortDescription"
          :option="data.selected.gallery"
          :item="data.item"
          :reviewsProduct="reviewsProduct"
          :variant="data.selected.variant"
        />
        <Form
          v-if="modal"
          @handle-close="handleClose"
          @select="select"
          :item="data"
          :shellComponent="shellComponent"
          :location="location"
          :edit="true"
          :key="instance"
          :url="url"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useProductStore } from './Composables/Store';
import { formatSecondaryProducts } from '@utils/format';
import { fetchStorefront, clean } from '@utils/GraphQLClient';
import { arraysEqual, deepClone } from '@src/js/utils/performance';
import {
  PRODUCT_QUERY,
  GALLERY_QUERY,
  SECONDARY_PRODUCTS_QUERY,
} from './Composables/Queries';
import { paginateVariants } from './Composables/Actions';

import Form from './Form.vue';
import Gallery from './Gallery.vue';
import Icon from '../Global/Icon.vue';
import { ugcVideo } from '@utils/global-functions';

const html = document.querySelector('html');
const template = document.querySelector('body');
const background = ref(false);
const modal = ref(false);
const data = ref(false);
const instance = ref(0);
const store = useProductStore();
const props = defineProps({
  product: { type: Object, required: true },
  url: { type: String, required: false },
  location: { type: String, required: false },
  reviewsProduct: { type: String, required: false },
  terms: { type: Object, required: false },
});

const emits = defineEmits(['toggle-modal']);

const shellComponent = computed(() => (isQuickView.value ? false : store.item));
const modalClasses = computed(() => {
  return isQuickView.value ? 'Modal--QV' : 'Modal--AutoBundle';
});

const isQuickView = computed(() => {
  return props.location === 'quick-view';
});

const shortDescription = computed(() => {
  if (!data.value) return false;
  const item = data?.value?.item;
  const hasShortDescription = item?.shortDescription ?? false;
  return hasShortDescription ? item.shortDescription.value : false;
});

const url = computed(() => {
  const item = data?.value?.item;
  const handle = item.handle.split('?')[0].trim();
  const base = `/products/${handle}`;
  const selectedVariant = isQuickView.value
    ? data.value.selected.variant.id
    : store.$state.selected.variant.id;

  if (!selectedVariant) return base;

  let params = '';

  if (selectedVariant) {
    const parsedId = selectedVariant.split('ProductVariant/')[1].trim();
    params = `?variant=${parsedId}`;
  }

  return `${base}${params}`;
});

const fetchProduct = async (handle) => {
  try {
    const response = await fetchStorefront({
      variables: {
        handle: handle,
      },
      query: PRODUCT_QUERY,
    });

    const item = clean(response.data.product) ?? false;

    if (!item) return false;

    const allVariants = await paginateVariants(
      response,
      item.id,
      item.variants
    );
    item.variants = allVariants;

    return deepClone(item);
  } catch (err) {
    console.error(err);
    return false;
  }
};

const fetchImageGalleryInfographic = (data) => {
  const metaobject = data.fields;
  const getField = (key) => metaobject.find((field) => field.key === key);

  const imageUrl = getField('image')?.object.image.url;
  const imageZoomUrl = getField('image')?.object.image.zoom;
  const infographicObj = {
    alt: `PDP2 Option[All] ${
      getField('gallery_position')?.value || 'LIFESTYLE'
    } METAOBJECT`,
    image: { url: imageUrl },
    zoom: { url: imageZoomUrl },
    infographic: true,
  };
  return infographicObj;
};

const fetchImageGalleryVideo = (data) => {
  const metaobject = data.fields;
  const getField = (key) => metaobject.find((field) => field.key === key);

  const videoSources = getField('video')?.object.sources;

  const posterImage = getField('poster_image')
    ? getField('poster_image')?.object.image.url
    : getField('video')?.object.previewImage.url;
  const subtitleFile = getField('subtitle')?.object.url;
  const videoObj = {
    alt: `PDP2 Option[All] ${
      getField('gallery_position')?.value || 'MAIN'
    } METAOBJECT`,
    sources: videoSources,
    previewImage: { url: posterImage },
    subtitles: subtitleFile,
    controls: {
      audio: JSON.parse(getField('audio')?.value),
      autoplay: JSON.parse(getField('autoplay')?.value),
      controls: JSON.parse(getField('controls')?.value),
    },
  };
  return videoObj;
};

const fetchGallery = async (handle) => {
  const data = await fetchStorefront({
    variables: {
      handle: handle,
    },
    query: GALLERY_QUERY,
  }).then((res) => {
    const data = clean(res?.data?.product?.media);
    const assets = data.edges.flatMap((img) => img.node);

    if (res?.data?.product?.image_gallery_infographic) {
      res?.data?.product?.image_gallery_infographic.metaobjects.nodes.forEach(
        (metaobject) => {
          assets.push(fetchImageGalleryInfographic(metaobject));
        }
      );
    }

    if (res?.data?.product?.image_gallery_video) {
      res?.data?.product?.image_gallery_video.metaobjects.nodes.forEach(
        (metaobject) => {
          assets.push(fetchImageGalleryVideo(metaobject));
        }
      );
    }
    return assets;
  });

  return data;
};

const fetchSecondaryItems = async (handle) => {
  const request = await fetchStorefront({
    variables: {
      handle: handle,
    },
    query: SECONDARY_PRODUCTS_QUERY,
  }).then(async (res) => {
    const response = formatSecondaryProducts(res);
    const hasItems = response?.handles && response?.handles.length > 0;
    if (!hasItems) return [];

    return await parseSecondaryItems(response);
  });

  return request;
};

const parseSecondaryItems = async (response = false) => {
  if (!response) return [];

  const haskey = (property, index) => {
    const keyPresent = Object.keys(response).includes(property) ?? false;
    if (!keyPresent) return false;

    const hasValueFromIndex = response[property][index] ?? false;
    return keyPresent && hasValueFromIndex;
  };

  const { handles } = response;

  const items = handles.reduce(async (arr, handle, index) => {
    const merge = haskey('merged', index) ? response?.merged[index] : false;
    if (!merge) return arr;

    const label = haskey('labels', index) ? response?.labels[index] : '';
    let limit = haskey('limits', index) ? response?.limits[index] : false;

    const item = await fetchProduct(handle);
    const gallery = await fetchGallery(handle);
    const { options, variants } = item;

    const colorIndex = options.findIndex((option) => option.name === 'Color');
    const mainColorIndex = data.value.item.options.findIndex(
      (option) => option.name === 'Color'
    );

    if (mainColorIndex < 0 || colorIndex < 0) return arr;
    const mainOptions = data.value.item.options;
    const evergreenSwatches = mainOptions[mainColorIndex].values.length;
    const colors = options[colorIndex].values;
    const swatches = limit ? colors.slice(0, limit) : colors;
    limit = swatches.length;

    const filteredSecondaryMedia = gallery.filter((asset) => {
      if (asset.alt !== '' && !asset.alt.includes('FEED')) {
        const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
        return swatches.includes(alt);
      }
    });

    const filteredMedia = data.value.gallery.filter((asset) => {
      if ((asset.alt !== '' && !asset.alt.includes('FEED')) || asset.sources) {
        const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
        return !swatches.includes(alt);
      }
    });

    item.limit = limit;
    item.label = label;
    data.value.gallery = [...filteredMedia, ...filteredSecondaryMedia];
    data.value.item.variants = [...data.value.item.variants, ...variants];
    arr.push(item);
    return arr;
  }, []);
  return items;
};

const fetchPromotedOptions = async () => {
  /**
   * Updates the `promotedOptionGroups` property of an item and its associated secondary items.
   *
   * @param {Function} store.formatPOGs - A method that formats `promotedOptionGroups` for a provided item.
   *
   * @description
   * - Checks if the main item's `promotedOptionGroups` property exists.
   * - Formats the `promotedOptionGroups` using `store.formatPOGs`.
   * - If secondary items are present, iterates over them to find and format their `promotedOptionGroups`.
   * - Combines the main item's formatted groups with those of the secondary items.
   * - Flattens and removes duplicates of the newly combined `promotedOptionGroups` array.
   */

  if (
    !data.value.item?.promotedOptionGroups ||
    data.value.secondaryItems.length <= 0
  )
    return;
  // Format and update the main item's promotedOptionGroups
  const groups = store.formatPOGs(data.value.item);
  data.value.item.promotedOptionGroups = groups;

  //Creates array of all promotedOptionGroups of the one or many secondary items and formats them.
  const secondaryGroups = data.value.secondaryItems.reduce((groups, item) => {
    if (item?.promotedOptionGroups) {
      const group = store.formatPOGs(item);
      groups.push(group);
    }
    return groups;
  }, []);

  // Merge main and secondary groups, flatten and remove duplicates
  const newGroup = Array.from(new Set([...groups, ...secondaryGroups.flat()]));
  return newGroup;
};

const fetchSelections = (payload = false) => {
  if (!payload) return false;
  const selected = {};
  const { selectedOptions } = payload?.variantBySelectedOptions
    ? payload?.variantBySelectedOptions
    : payload;
  const { id } = payload?.variantBySelectedOptions
    ? payload?.variantBySelectedOptions
    : payload.selectedVariant;

  const secondaryItems = data?.value?.secondaryItems ?? [];

  const secondaryItem =
    secondaryItems.find((item) => {
      const matchingVariant =
        item.variants.find((variant) => {
          return variant.id === id;
        }) ?? false;
      return matchingVariant;
    }) ?? false;

  const item = secondaryItem ? secondaryItem : deepClone(data.value.item);

  const selectedGallery = selectedOptions.findIndex((option) => {
    return option.name === data.value.galleryOption;
  });

  selected.handle = item.handle;
  selected.matches = [];
  selected.options = selectedOptions;
  selected.gallery = selectedOptions[selectedGallery].value;

  selectedOptions.forEach((selection) => {
    const matches = data.value.item.variants.filter((variant) => {
      const { selectedOptions } = variant;
      const match = selectedOptions.find(
        (option) => option.value === selection.value
      );
      return match;
    });
    selected.matches.push(matches);
  });

  selected.matches = selected.matches.flat();

  // if no payload.variantBySelectedOptions, returns matched selected variant from data.value.items & set store.state.selected to selected variant
  const setDataMatches = () => {
    const matched = selected.matches.find((match) => match.id == id);
    if (!data.value.item.selected) {
      data.value.item.selected = {
        variant: matched,
      };
    }
    return matched;
  };

  selected.variant = payload.variantBySelectedOptions
    ? payload.variantBySelectedOptions
    : setDataMatches();

  return selected;
};

const fetchAdditionalProperties = () => {
  const item = data.value.item ?? false;
  if (!item) return;

  const tags = item.tags ?? false;
  const galleryTag = tags.filter((tag) => tag.includes('Gallery Option:'));
  const galleryOption = galleryTag[0].split(':')[1].trim();
  const modalTags = tags
    .filter((tag) => tag.includes('PDP Modal'))
    .map((tag) => tag.split(':')[1].trim());

  const optionName = galleryOption.includes('Default')
    ? 'Title'
    : galleryOption;

  const index = item.options.findIndex((option) => option.name === optionName);
  if (isQuickView.value) data.value.item.galleryIndex = index;
  return {
    galleryOption: optionName,
    galleryIndex: index,
    modals: modalTags,
  };
};

const select = (payload) => {
  const { index, option } = payload;
  const options = JSON.parse(JSON.stringify(data.value.selected.options));
  options[index] = option;
  instance.value += 1;

  const updateGallery = option.name === data.value.galleryOption;
  const variants = data.value.item.variants;
  const selections = options.filter((option) => option !== '');
  data.value.selected.matches = [];

  selections.forEach((selection) => {
    const matches = variants.filter((variant) => {
      const { selectedOptions } = variant;
      const match = selectedOptions.find(
        (option) => option.value === selection.value
      );
      return match;
    });
    data.value.selected.matches.push(matches);
  });

  data.value.selected.matches = data.value.selected.matches.flat();
  data.value.item.fromPrice.value = store.updateFromPrice(data.value.selected);

  const variant = data.value.selected.matches.find((match) => {
    const selections = options;
    return arraysEqual(match.selectedOptions, selections);
  });

  if (updateGallery) data.value.selected.gallery = option.value;

  const secondaryItem =
    data.value.secondaryItems.find((item) => item.id === variant.product.id) ??
    false;
  const item = secondaryItem ? secondaryItem : data.value.item;

  data.value.selected.options = JSON.parse(JSON.stringify(options));
  data.value.selected.handle = item.handle;
  data.value.selected.variant = variant;
  store.updateAutoPrices(variant, item.tags);
};

const handleClose = () => {
  template.style.top = '';
  window.scrollTo({
    top: store.$state.scrollPosition,
    left: 0,
    behavior: 'instant',
  });

  modal.value = false;
  background.value = false;
  html.classList.remove('overflow-hidden');
  template.classList.remove('overflow-hidden');
  emits('toggle-modal');
  if (!isQuickView.value) {
    const tags = getAllProductTags(
      store.$state.item,
      store.$state.secondaryItems
    );
    store.updateHiddenOptions([]);
    store.updateHiddenOptions(tags);
    store.$state.edit = false;
  } else {
    store.$state.isQV = false;
  }

  ugcVideo('show');
};

const getAllProductTags = (item, secondaryItems = []) => {
  const tags = item?.tags ?? [];
  const secondaryTags = secondaryItems.flatMap((item) => item.tags) ?? [];
  return [...tags, ...secondaryTags];
};

const setEvergreenTags = () => {
  const tags = data?.value?.item?.tags ?? false;
  const evergreenSwatchTag = tags.filter((tag) => tag.includes('Evergreen:'));
  if (evergreenSwatchTag.length > 0) {
    data.value.evergreenSwatchCount = parseInt(
      evergreenSwatchTag[0].split(':')[1].trim()
    );
  }
};

onMounted(async () => {
  ugcVideo('hide');
  const handle = props.product.handle.replace('-seasonal', '');

  data.value = {};
  background.value = true;
  data.value.editing = true;
  data.value.terms = props.terms ? props.terms : store.terms;
  data.value.item = await fetchProduct(handle);
  data.value.gallery = await fetchGallery(handle);

  data.value = { ...data.value, ...fetchAdditionalProperties() };
  data.value.secondaryItems = await fetchSecondaryItems(handle);
  data.value.selected = fetchSelections(props.product);
  if (props.product?.editIndex !== undefined) {
    data.value.selected.editIndex = props.product.editIndex;
  }
  const tags = getAllProductTags(data.value.item, data.value.secondaryItems);

  store.updateHiddenOptions([]);
  store.updateHiddenOptions(tags);

  if (props.terms && !store.$state.terms) {
    store.$state.terms = props.terms;
  }

  if (!isQuickView.value) store.$state.edit = data.value;
  else store.$state.isQV = true;
  setEvergreenTags();

  data.value.item.promotedOptionGroups = await fetchPromotedOptions();

  modal.value = true;
  html.classList.add('overflow-hidden');
  template.classList.add('overflow-hidden');
  store.$state.scrollPosition = window.scrollY;
  if (isQuickView.value) {
    store.promoReady = true;
  }
});
</script>
